import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { SpinLoading } from 'antd-mobile'
import { CheckRecord, CheckType, HazardLevel } from '@/types'
import { getAllCheckRecords, deleteCheckRecord } from '@/services/storage'
import { formatDate } from '@/utils/helpers'

export const HistoryPage: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [records, setRecords] = useState<CheckRecord[]>([])

  useEffect(() => {
    loadRecords()
  }, [])

  const loadRecords = async () => {
    setLoading(true)
    try {
      const data = await getAllCheckRecords()
      setRecords(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()))
    } catch (error) {
      console.error('加载历史记录失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewReport = (id: string) => {
    navigate(`/report/${id}`)
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('确定要删除此记录吗？')) {
      try {
        await deleteCheckRecord(id)
        setRecords(records.filter((r) => r.id !== id))
      } catch (error) {
        console.error('删除失败:', error)
      }
    }
  }

  const getTypeName = (type: CheckType) => {
    return type === CheckType.LIQUEFIED_GAS ? '瓶装液化石油气' : '管道天然气'
  }

  const getHazardCount = (record: CheckRecord) => {
    return record.selectedItems?.length || 0
  }

  const getMajorCount = (record: CheckRecord) => {
    return record.selectedItems?.filter((item) => item.hazardLevel === HazardLevel.MAJOR).length || 0
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <SpinLoading color="#c62828" />
        <p className="mt-4 text-gray-600">正在加载历史记录...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部标题栏 */}
      <div className="bg-red-700 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center">
          <button onClick={() => navigate(-1)} className="mr-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <h1 className="text-lg font-bold">历史记录</h1>
        </div>
      </div>

      {/* 记录列表 */}
      <div className="p-4">
        {records.length === 0 ? (
          <div className="text-center py-12">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="mx-auto text-gray-300 mb-4">
              <path
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-gray-400">暂无历史记录</p>
          </div>
        ) : (
          <div className="space-y-3">
            {records.map((record) => (
              <div key={record.id} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          record.checkType === CheckType.LIQUEFIED_GAS
                            ? 'bg-red-100 text-red-600'
                            : 'bg-orange-100 text-orange-600'
                        }`}
                      >
                        {getTypeName(record.checkType)}
                      </span>
                    </div>
                    <div className="text-base font-medium text-gray-800">
                      {record.inspectedUnit}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">{record.inspectedAddress}</div>
                  </div>
                  <button
                    onClick={() => handleDelete(record.id)}
                    className="text-gray-400 hover:text-red-500 p-1"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="text-gray-500">
                    <span>检查人：{record.inspector}</span>
                    <span className="mx-2">|</span>
                    <span>{formatDate(record.checkDate)}</span>
                  </div>
                  <div className="text-gray-500">
                    隐患 <span className="text-red-600 font-medium">{getHazardCount(record)}</span> 项
                    {getMajorCount(record) > 0 && (
                      <span className="ml-1 text-red-600">(重大 {getMajorCount(record)})</span>
                    )}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                  <button
                    onClick={() => handleViewReport(record.id)}
                    className="text-red-600 text-sm font-medium"
                  >
                    查看报告 →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
